/**
 * AI Usage Policy Page
 * 
 * Legal document for NewAudia Systems, LLC
 * Version 1.1 | Effective Date: March 1, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Bot, Mail } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { useAuth } from '../contexts/AuthContext';
import { Seo, PAGE_SEO } from '../components/Seo';

const AIUsagePolicyPage = () => {
  const { user } = useAuth();
  
  const getHomeRoute = () => {
    if (!user) return '/';
    const roles = user.roles || [];
    if (roles.includes('admin')) return '/admin';
    if (roles.includes('publisher')) return '/publisher';
    if (roles.includes('author') || roles.includes('creator')) return '/creator';
    if (roles.includes('reader')) return '/home';
    return '/';
  };
  
  const homeRoute = getHomeRoute();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Seo {...PAGE_SEO.aiPolicy} />
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center gap-3">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-10 w-auto rounded-lg" />
          </Link>
          <Link 
            to={homeRoute}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Document Header */}
        <div className="mb-12 pb-8 border-b border-slate-700">
          <div className="flex items-center gap-4 mb-6">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-16 w-auto rounded-lg" />
            <div>
              <h1 data-testid="ai-policy-company-name" className="text-3xl font-bold text-white">NewAudia Systems, LLC</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <Bot className="w-6 h-6 text-indigo-400" />
            <h2 data-testid="ai-policy-title" className="text-2xl font-semibold text-white">AI Usage Policy</h2>
          </div>
          
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <p><span className="text-slate-500">Effective Date:</span> March 1, 2026</p>
            <p><span className="text-slate-500">Version:</span> 1.1</p>
            <p><span className="text-slate-500">Last Updated:</span> March 2026</p>
          </div>
        </div>

        {/* Policy Content */}
        <div className="prose prose-invert prose-slate max-w-none">
          {/* Important Notice */}
          <section className="mb-10">
            <p className="text-amber-400 font-semibold text-sm uppercase tracking-wide mb-4">Important — Please Read Carefully</p>
            <p className="text-slate-300 leading-relaxed">
              This AI Usage Policy ("Policy") governs the use of artificial intelligence-assisted features within the 
              NewAudia platform, website, applications, and services (collectively, the "Services"). This Policy applies 
              to ALL users, including Readers, Authors, Publishers, and Administrators. By accessing or using any AI-assisted 
              feature within the Services, you agree to this Policy in full.
            </p>
          </section>

          {/* Company Identification */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Company Identification</h3>
            <p className="text-slate-300 leading-relaxed">
              The Services are operated by <strong className="text-white">NewAudia Systems, LLC</strong>, a limited liability 
              company organized under the laws of the State of Colorado, with its principal place of business in Colorado Springs, 
              Colorado (hereinafter "NewAudia," "Company," "we," "us," or "our"). "NewAudia" is a registered trade name of 
              NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-slate-300 leading-relaxed mt-4">
              This Policy is incorporated by reference into the NewAudia Terms of Service and the NewAudia Acceptable Use 
              Policy. For Authors and Publishers, it is additionally incorporated into the NewAudia Author Publishing Agreement. 
              In the event of a conflict between this Policy and the Author Publishing Agreement with respect to AI content 
              processing of Manuscripts, the Author Publishing Agreement controls.
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">1. Overview of AI Features on the NewAudia Platform</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              NewAudia employs artificial intelligence technologies across two distinct functional areas of the platform. 
              Users should understand what each feature does, how it uses content, and what limitations apply.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.1 Audia — AI Reading Assistant</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              Audia is NewAudia's built-in AI reading assistant, embedded within each published Audiobook. Audia allows 
              readers to ask questions about the content of any Audiobook they are actively listening to — including 
              questions about plot, characters, themes, and context. Audia responds using information derived exclusively 
              from the Manuscript associated with that Audiobook.
            </p>
            <p className="text-slate-300 leading-relaxed mb-3">
              Audia is not a general-purpose AI assistant and does not draw on knowledge outside the content of the 
              specific title being accessed.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Audia operates using a Retrieval-Augmented Generation (RAG) architecture. When a reader submits a question, 
              Audia retrieves relevant passages from the Manuscript's indexed content (referred to in the Author Publishing 
              Agreement as "Ephemeral Intelligence") and generates a response grounded in that content. Responses are 
              generated via third-party AI language model services, currently provided by OpenAI. See Section 5 for 
              third-party disclosure.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">1.2 AI Narration Pipeline</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              When Authors or Publishers submit a Manuscript for ingestion, NewAudia's seven-step automated pipeline 
              converts the EPUB into an AI-narrated Audiobook. This process includes automated text segmentation, outline 
              generation, and audio synthesis via OpenAI's Text-to-Speech (TTS) API. The resulting narration is a 
              computer-generated voice performance derived entirely from the submitted Manuscript text.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Authors acknowledge that AI narration does not guarantee a specific voice, pacing, or stylistic outcome, 
              consistent with Section 2.5 of the Author Publishing Agreement. The AI narration pipeline is a production 
              process, not an AI assistant feature, and no reader-facing AI interaction occurs during narration generation.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">2. Author Disclosures — AI Processing of Manuscripts</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              This section applies specifically to Authors and Publishers who submit Manuscripts to the NewAudia platform. 
              By submitting a Manuscript, Authors grant NewAudia a non-exclusive license to process that content through AI 
              systems as described below, consistent with Article 4 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.1 Ephemeral Intelligence Generation</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              Upon ingestion, NewAudia generates "Ephemeral Intelligence" data from each Manuscript. Ephemeral Intelligence 
              consists of AI-generated indexing, segmentation metadata, and conversational context data derived from the 
              Manuscript text. This data is used exclusively to power the Audia AI reading assistant for readers of that title.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Ephemeral Intelligence is not shared with third parties for commercial purposes, except as necessary to operate 
              the platform (e.g., API calls to OpenAI for query processing). The term "Ephemeral Intelligence" is defined in 
              Article 1 of the Author Publishing Agreement. Authors who have executed that Agreement have already consented 
              to this processing under Section 4.4 of that document. This Policy provides supplemental transparency about 
              how that process works in practice.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.2 AI Narration Processing</h4>
            <p className="text-slate-300 leading-relaxed">
              Manuscript text is transmitted to OpenAI's TTS API to generate audio narration. This transmission is governed 
              by NewAudia's agreement with OpenAI and is subject to OpenAI's data processing terms. NewAudia does not authorize 
              OpenAI to use Manuscript content to train general-purpose AI models under the terms of its API agreement. Authors 
              acknowledge that this processing is a necessary technical step in Audiobook production on the platform.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.3 Data Retention on Manuscript Removal</h4>
            <p className="text-slate-300 leading-relaxed">
              If an Author removes a Manuscript from the platform or terminates their Author Publishing Agreement, NewAudia 
              will use commercially reasonable efforts to remove the Audiobook from public access within thirty (30) business 
              days, consistent with Section 7.2 of the Author Publishing Agreement. Ephemeral Intelligence data and associated 
              metadata may be retained by NewAudia for a reasonable period for archival, legal, or compliance purposes 
              following removal, as permitted under that Agreement.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">3. Reader Disclosures — Using Audia</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              This section applies to Readers using the Audia AI reading assistant feature while accessing 
              Audiobooks on the NewAudia platform.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.1 How Audia Generates Responses</h4>
            <p className="text-slate-300 leading-relaxed">
              Audia's responses are generated algorithmically based on the content of the Manuscript associated with the 
              Audiobook you are accessing. Responses are not pre-written by the Author or NewAudia staff. Because AI-generated 
              responses are produced by automated systems, they may be incomplete, imprecise, or may not reflect nuances 
              present in the full Manuscript.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.2 Limitations of Audia</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              Audia is an assistive reading tool only. Audia does not have access to content outside the specific 
              Audiobook being accessed. Audia's responses do not constitute legal, medical, financial, professional, 
              or any other form of regulated advice.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Readers are solely responsible for any decisions made in reliance on Audia-generated responses. 
              NewAudia does not warrant the accuracy, completeness, or fitness for any particular purpose of 
              any Audia output.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.3 Reader Query Processing</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              When a reader submits a question to Audia, that query — along with relevant context retrieved from the 
              Manuscript's Ephemeral Intelligence index — is transmitted to OpenAI's API to generate a response.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Reader queries are not stored for purposes of training generalized AI models. Reader query data may be 
              retained in platform logs for quality assurance, abuse prevention, and system improvement purposes 
              consistent with the NewAudia Privacy Policy.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">4. General User Acknowledgments</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              By using any AI-assisted feature within the Services, all users acknowledge and agree that:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>AI outputs are generated algorithmically and are not authored, reviewed, or endorsed by NewAudia or the Author of the relevant Manuscript unless explicitly stated</li>
              <li>AI outputs may be incomplete, inaccurate, contextually limited, or outdated</li>
              <li>AI outputs do not constitute legal, medical, financial, or professional advice of any kind</li>
              <li>Users are solely responsible for any actions, decisions, or reliance placed on AI-generated outputs</li>
              <li>AI-generated outputs do not confer intellectual property ownership rights to the user</li>
              <li>The performance, availability, and specific behavior of AI features may change at any time as the underlying models and platform infrastructure evolve</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">5. Third-Party AI Services</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              NewAudia uses third-party artificial intelligence services to power the features described in this Policy. 
              Users should be aware of the following third-party processors currently engaged:
            </p>
            <div className="overflow-x-auto mb-6">
              <table data-testid="ai-policy-provider-table" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-40">Provider</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Role on the Platform</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">OpenAI</td>
                    <td className="py-3 px-4 text-slate-400">AI narration (Text-to-Speech API) for Audiobook production; language model API for Audia AI reading assistant query processing.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">NewAudia Platform</td>
                    <td className="py-3 px-4 text-slate-400">RAG-based Ephemeral Intelligence indexing, retrieval, and Audia query routing. All indexing is performed on NewAudia's own infrastructure.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia selects third-party AI providers based on capability, reliability, and data handling commitments. 
              NewAudia does not authorize third-party providers to use Manuscript content or reader query data to train 
              general-purpose AI models under the terms of its current service agreements.
            </p>
            <p className="text-slate-300 leading-relaxed">
              NewAudia will update this Policy if its third-party AI provider relationships change materially.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">6. Prohibited AI Use</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              Users may not use AI-assisted features within the Services to:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Generate, submit, or distribute unlawful, harmful, defamatory, or deceptive content</li>
              <li>Impersonate any real individual, entity, or brand</li>
              <li>Circumvent, disable, probe, or exploit any AI safeguard, content filter, or platform security control</li>
              <li>Automate abusive, fraudulent, or exploitative interactions with the platform, including artificial inflation of engagement metrics or Commission calculations</li>
              <li>Attempt to extract, reconstruct, or replicate any Manuscript's full text or proprietary AI-generated outputs through repeated querying or systematic interaction with Audia</li>
              <li>Use Audia or any AI feature to facilitate any activity prohibited under the NewAudia Acceptable Use Policy or Author Publishing Agreement</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">7. Modifications and Availability of AI Features</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia reserves the right to modify, limit, suspend, or permanently discontinue any AI-assisted feature 
              at any time, with or without notice, and without liability to any user.
            </p>
            <p className="text-slate-300 leading-relaxed">
              Changes to AI features may result from updates to third-party provider capabilities, changes in applicable 
              law or regulation, platform infrastructure decisions, or quality and safety considerations. NewAudia will 
              use commercially reasonable efforts to communicate material changes to AI features that affect the core 
              functionality described in this Policy.
            </p>
          </section>

          {/* Section 8 - Disclaimer */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">8. Disclaimer of Warranties</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
              All AI-assisted features are provided "as is" and "as available" without warranty of any kind, whether 
              express, implied, or statutory. NewAudia disclaims all warranties with respect to AI features, including 
              but not limited to implied warranties of merchantability, fitness for a particular purpose, accuracy, and 
              non-infringement. NewAudia does not warrant that AI-generated outputs will be accurate, complete, reliable, 
              or suitable for any particular purpose.
            </p>
          </section>

          {/* Section 9 - Liability */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">9. Limitation of Liability</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide mb-3">
              To the maximum extent permitted by applicable law, NewAudia's total cumulative liability to any user for 
              any claim arising under or related to the use of AI-assisted features shall not exceed fifty dollars 
              ($50.00 USD). NewAudia shall not be liable for any indirect, incidental, special, consequential, exemplary, 
              or punitive damages arising from reliance on or use of any AI-generated output, even if advised of the 
              possibility of such damages.
            </p>
            <p className="text-slate-300 leading-relaxed text-sm">
              For Authors, the limitation of liability applicable to the Author Publishing Agreement (Section 6.3 thereof) 
              governs claims related to Manuscript ingestion and AI narration processing.
            </p>
          </section>

          {/* Section 10 - Contact */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">10. Contact Information</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              For questions, concerns, or reports regarding this AI Usage Policy or the AI-assisted features 
              described herein, please contact NewAudia using the information below.
            </p>
            <div data-testid="ai-policy-contact-info" className="bg-slate-900/50 border border-slate-700 rounded-lg p-6">
              <p className="text-white font-semibold text-lg">NewAudia Systems, LLC</p>
              <p className="text-slate-400 text-sm">A Colorado Limited Liability Company</p>
              <p className="text-slate-400 text-sm">"NewAudia" is a registered trade name in Colorado</p>
              <p className="text-slate-400 mt-2">Colorado Springs, Colorado, USA</p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">General Inquiries & Policy Questions:</span>
                  <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    info@newaudia.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">Administrative & Legal Notices:</span>
                  <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    admin@newaudia.com
                  </a>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-3">
                Website: <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              </p>
            </div>
          </section>

          {/* Section 11 - Policy Updates */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">11. Policy Updates</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              NewAudia may update this AI Usage Policy at any time by posting a revised version on the Services. NewAudia 
              will use reasonable efforts to notify users of material changes via email to the address on file. Continued 
              use of any AI-assisted feature following notice of any update constitutes acceptance of the revised Policy.
            </p>

            <h4 className="text-lg font-medium text-white mb-3">Version History</h4>
            <div className="overflow-x-auto">
              <table data-testid="ai-policy-version-history" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-20">Version</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-36">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Summary of Changes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">1.0</td>
                    <td className="py-3 px-4 text-slate-400">January 1, 2026</td>
                    <td className="py-3 px-4 text-slate-400">Initial publication. Entity listed as NewAudia dba Use What You Got Publishing, LLC. General AI feature disclosures only.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">1.1</td>
                    <td className="py-3 px-4 text-slate-300">March 2026</td>
                    <td className="py-3 px-4 text-slate-400">
                      Entity updated to NewAudia Systems, LLC (registered trade name in Colorado). Added named feature 
                      disclosures for Audia and AI Narration Pipeline; Author-specific AI processing disclosures (Ephemeral 
                      Intelligence, TTS, data retention); Reader-specific Audia disclosures; third-party provider table 
                      (OpenAI); prohibited AI use enumeration; disclaimer of warranties; limitation of liability aligned 
                      with AUP v1.1; cross-references to Author Publishing Agreement v1.0 and AUP v1.1; version history 
                      table. Removed editorial closing statement.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Footer Entity Statement */}
          <section className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              NewAudia Systems, LLC | Colorado Springs, Colorado |{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              {' '}| <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">admin@newaudia.com</a>
            </p>
            <p className="text-sm text-slate-500 mt-2 italic">
              "NewAudia" is a registered trade name of NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Version 1.1 — March 2026
            </p>
          </section>
        </div>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default AIUsagePolicyPage;
